import { apiClient } from './api';
import type {
    CreateProviderInvitationPayload,
    CreateProviderOnboardingPayload,
    ProviderContextResponse,
    ProviderContextsResponse,
    ProviderMemberResponse,
    ProviderInvitationResponse,
    ProviderInvitationOnboardingPayload,
    ProviderInvitationOnboardingResponse,
    ProviderInvitationPreviewResponse,
    ProviderInvitationsResponse,
    ProviderOnboardingResponse,
    ProviderBasesResponse,
    ProviderMembersResponse,
    ProviderBasePayload,
    ProviderBaseResponse,
    ProviderPermissionOverrides,
    TransportProviderMemberRole,
} from '../types';

export const providerService = {
    listProviderContexts: async (): Promise<ProviderContextsResponse> => {
        return apiClient.get<ProviderContextsResponse>('/api/providers/contexts', true);
    },

    selectProviderContext: async (providerId: number): Promise<ProviderContextResponse> => {
        return apiClient.post<ProviderContextResponse>('/api/providers/contexts/select', {
            transport_provider_id: providerId,
        }, true);
    },

    createProviderOnboarding: async (
        payload: CreateProviderOnboardingPayload
    ): Promise<ProviderOnboardingResponse> => {
        return apiClient.post<ProviderOnboardingResponse>('/api/providers/onboarding', payload, true);
    },

    listProviderMembers: async (providerId: number): Promise<ProviderMembersResponse> => {
        return apiClient.get<ProviderMembersResponse>(`/api/providers/${providerId}/members`, true);
    },

    updateProviderMember: async (
        providerId: number,
        memberId: number,
        payload: {
            role?: Exclude<TransportProviderMemberRole, 'owner'>;
            permissions?: ProviderPermissionOverrides | null;
        }
    ): Promise<ProviderMemberResponse> => {
        return apiClient.put<ProviderMemberResponse>(
            `/api/providers/${providerId}/members/${memberId}`,
            payload,
            true,
        );
    },

    approveProviderMember: async (
        providerId: number,
        memberId: number
    ): Promise<ProviderMemberResponse> => {
        return apiClient.post<ProviderMemberResponse>(
            `/api/providers/${providerId}/members/${memberId}/approve`,
            {},
            true,
        );
    },

    createProviderInvitation: async (
        providerId: number,
        payload: CreateProviderInvitationPayload
    ): Promise<ProviderInvitationResponse> => {
        return apiClient.post<ProviderInvitationResponse>(
            `/api/providers/${providerId}/invitations`,
            payload,
            true,
        );
    },

    listProviderInvitations: async (providerId: number): Promise<ProviderInvitationsResponse> => {
        return apiClient.get<ProviderInvitationsResponse>(`/api/providers/${providerId}/invitations`, true);
    },

    getProviderInvitationLink: async (
        providerId: number,
        invitationId: number
    ): Promise<ProviderInvitationResponse> => {
        return apiClient.post<ProviderInvitationResponse>(
            `/api/providers/${providerId}/invitations/${invitationId}/link`,
            {},
            true,
        );
    },

    resendProviderInvitation: async (
        providerId: number,
        invitationId: number
    ): Promise<ProviderInvitationResponse> => {
        return apiClient.post<ProviderInvitationResponse>(
            `/api/providers/${providerId}/invitations/${invitationId}/resend`,
            {},
            true,
        );
    },

    listProviderBases: async (providerId: number): Promise<ProviderBasesResponse> => {
        return apiClient.get<ProviderBasesResponse>(`/api/providers/${providerId}/bases`, true);
    },

    createProviderBase: async (providerId: number, payload: ProviderBasePayload): Promise<ProviderBaseResponse> => {
        return apiClient.post<ProviderBaseResponse>(`/api/providers/${providerId}/bases`, payload, true);
    },

    updateProviderBase: async (
        providerId: number,
        baseId: number,
        payload: Partial<ProviderBasePayload>
    ): Promise<ProviderBaseResponse> => {
        return apiClient.put<ProviderBaseResponse>(`/api/providers/${providerId}/bases/${baseId}`, payload, true);
    },

    deactivateProviderBase: async (providerId: number, baseId: number): Promise<ProviderBaseResponse> => {
        return apiClient.delete<ProviderBaseResponse>(`/api/providers/${providerId}/bases/${baseId}`, true);
    },

    cancelProviderInvitation: async (
        providerId: number,
        invitationId: number
    ): Promise<ProviderInvitationResponse> => {
        return apiClient.post<ProviderInvitationResponse>(
            `/api/providers/${providerId}/invitations/${invitationId}/cancel`,
            {},
            true,
        );
    },

    acceptProviderInvitation: async (token: string): Promise<ProviderInvitationResponse> => {
        return apiClient.post<ProviderInvitationResponse>(`/api/provider-invitations/${token}/accept`, {}, true);
    },

    getProviderInvitation: async (token: string): Promise<ProviderInvitationPreviewResponse> => {
        return apiClient.get<ProviderInvitationPreviewResponse>(`/api/provider-invitations/${token}`, false);
    },

    completeProviderInvitationOnboarding: async (
        token: string,
        payload: ProviderInvitationOnboardingPayload
    ): Promise<ProviderInvitationOnboardingResponse> => {
        return apiClient.post<ProviderInvitationOnboardingResponse>(
            `/api/provider-invitations/${token}/onboarding`,
            payload,
            false,
        );
    },
};
